import type {
  Market,
  Outcome,
  PublishedEventListItem,
  PublishedEventParticipants,
  SportsType,
  TopMarketOutcomeType,
  TopMarketTypeDisplay,
} from '../@types';
import { getDisplayName } from '../utils';
import { useEventCard } from './useEventCard';
import type { PreMatchWidgetSeedEvent } from '../components/widgets/pre-match-widget/PreMatchWidgetModal';
import useMatchWidgetConfig from './useMatchWidgetConfig';

export type FeaturedEventCardModel = {
  id: string;
  name: string;
  startDate: string;
  participants: PublishedEventParticipants;
  sportId: string;
  sportIcon?: SportsType | null;
  competitionName?: string | null;
  competitionShortName?: string | null;
  tournamentName?: string | null;
  statsId?: string | null;
  hasStatistics: boolean;
  totalMarkets: number;
  isEventActive?: boolean;
  topMarket?: Market;
  outcomeTypes?: TopMarketOutcomeType[];
  statsEvent?: PreMatchWidgetSeedEvent | null;
  handleContentClick: () => void;
  addToBetslip: (outcome: Outcome) => void;
  getOutcomeForType: (outcomeTypeId: string) => Outcome | undefined;
};

export type DisplayedOutcome = { id: string; outcome: Outcome | undefined };

/**
 * The single source of truth for which outcomes a featured card renders, in what
 * order, with what fallback. Prefer the ordered outcome types; fall back to the raw
 * top-market outcomes when the typed list is empty OR none of them resolve to a live
 * outcome. Column-aligned tables do NOT use this — they render via the shared EventsTable.
 */
export const selectDisplayedOutcomes = (model: FeaturedEventCardModel): DisplayedOutcome[] => {
  const ordered =
    model.outcomeTypes?.map((outcomeType) => ({
      id: outcomeType.id,
      outcome: model.getOutcomeForType(outcomeType.id),
    })) ?? [];

  if (ordered.length && ordered.some(({ outcome }) => !!outcome)) {
    return ordered;
  }

  return model.topMarket?.outcomes?.map((outcome) => ({ id: outcome.id, outcome })) ?? [];
};

export const usePublishedFeaturedEventCardModel = (
  event: PublishedEventListItem,
  topMarketTypes?: TopMarketTypeDisplay[]
): FeaturedEventCardModel => {
  const { startDate, participants, topMarket, outcomeTypes, addToBetslip, handleContentClick, getOutcomeForType } =
    useEventCard(event, topMarketTypes);
  const competitionName = event.competition?.name ?? event.tournament?.competition?.name;
  const competitionShortName = event.competition?.shortName ?? event.tournament?.competition?.shortName;
  const { isWidgetAvailable } = useMatchWidgetConfig('preMatch', event.tournament?.sport?.id ?? '');

  return {
    id: event.id,
    name: event.name,
    startDate,
    participants,
    sportId: event.tournament.sport.id,
    sportIcon: event.tournament.sport.icon,
    competitionName,
    competitionShortName,
    tournamentName: event.tournament.parentTournamentName ?? getDisplayName(event.tournament),
    statsId: event.statsId,
    hasStatistics: !!event.statsId && isWidgetAvailable,
    totalMarkets: event.totalMarkets,
    isEventActive: event.isActive,
    topMarket,
    outcomeTypes,
    statsEvent: event,
    handleContentClick,
    addToBetslip,
    getOutcomeForType,
  };
};
